import { useState, useEffect } from 'react'
import axios from 'axios'
import { baseURL } from '../../config'

function GeneralSettings({ isDarkMode }) {
	const [name, setName] = useState('')
	const [savedName, setSavedName] = useState('')
	const [email, setEmail] = useState('')
	const [profileLoading, setProfileLoading] = useState(true)
	const [nameLoading, setNameLoading] = useState(false)
	const [nameMessage, setNameMessage] = useState('')
	const [nameError, setNameError] = useState('')
	const [editingName, setEditingName] = useState(false)
	const textStyle = { color: isDarkMode ? 'white' : 'black' }

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				const res = await axios.get(`${baseURL}/api/user/profile`)
				setName(res.data.name)
				setSavedName(res.data.name)
				setEmail(res.data.email)
			} catch (err) {
				console.error('Error fetching profile:', err)
			} finally {
				setProfileLoading(false)
			}
		}
		fetchProfile()
	}, [])

	useEffect(() => {
		if (!nameMessage && !nameError) return
		const timer = setTimeout(() => {
			setNameMessage('')
			setNameError('')
		}, 5000)
		return () => clearTimeout(timer)
	}, [nameMessage, nameError])

	const nameHasChanged = name.trim() !== savedName

	const handleNameSave = async () => {
		const trimmedName = name.trim()

		if (!trimmedName) {
			setNameError('Name is required')
			return
		}

		if (trimmedName.length < 2) {
			setNameError('Name must be at least 2 characters')
			return
		}

		setNameLoading(true)
		setNameError('')
		setNameMessage('')

		try {
			const res = await axios.put(`${baseURL}/api/user/update-name`, { name: trimmedName })
			setName(res.data.name)
			setSavedName(res.data.name)
			setEditingName(false)
			setNameMessage('Name updated')
		} catch (err) {
			setNameError(err.response?.data?.error || 'Failed to update name')
		} finally {
			setNameLoading(false)
		}
	}

	return (
		<div>
			{profileLoading ? (
				<div className="flex justify-center items-center py-32">
					<div
						className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
							isDarkMode ? 'border-gray-400' : 'border-gray-500'
						}`}
					/>
				</div>
			) : (
				<div>
					<div
						className="rounded-md p-6 text-white shadow-lg max-w-lg mx-auto"
						style={{ maxWidth: '800px' }}
					>
						<h2 style={textStyle} className="text-lg font-semibold mb-4">
							Profile
						</h2>

						<div className="mb-6">
							<div className="flex items-center gap-3">
								<label
									style={textStyle}
									className="text-sm font-medium"
								>
									Name:
								</label>
								<div className="flex items-center gap-2">
									{editingName ? (
										<>
											<input
												type="text"
												value={name}
												onChange={e => {
													setName(e.target.value)
													setNameError('')
													setNameMessage('')
												}}
												onKeyDown={e => {
													if (e.key === 'Enter') handleNameSave()
													if (e.key === 'Escape') {
														setName(savedName)
														setEditingName(false)
														setNameError('')
													}
												}}
												maxLength={56}
												autoFocus
												className={`px-2 py-1 rounded border text-sm ${
													isDarkMode
														? 'bg-gray-700 border-gray-600 text-white'
														: 'bg-white border-gray-300 text-gray-900'
												} focus:outline-none focus:ring-2 focus:ring-blue-500`}
											/>
											<button
												onClick={handleNameSave}
												disabled={nameLoading}
												className="p-1 text-green-500 hover:text-green-400"
											>
												<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											</button>
											<button
												onClick={() => {
													setName(savedName)
													setEditingName(false)
													setNameError('')
												}}
												className="p-1 text-red-500 hover:text-red-400"
											>
												<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
												</svg>
											</button>
										</>
									) : (
										<>
											<span
												className={`text-sm ${
													isDarkMode ? 'text-white' : 'text-gray-900'
												}`}
											>
												{savedName}
											</span>
											<button
												onClick={() => {
													setName(savedName)
													setEditingName(true)
												}}
												className={`p-1 rounded hover:bg-gray-700/50 ${
													isDarkMode
														? 'text-gray-400 hover:text-gray-300'
														: 'text-gray-500 hover:text-gray-700'
												}`}
												title="Edit name"
											>
												<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
												</svg>
											</button>
										</>
									)}
								</div>
							</div>
							{nameError && (
								<p className="text-red-500 text-sm mt-1">{nameError}</p>
							)}
							{nameMessage && (
								<p className="text-green-500 text-sm mt-1">{nameMessage}</p>
							)}
						</div>

						<div className="mb-6">
							<div className="flex items-center gap-3">
								<label
									style={textStyle}
									className="text-sm font-medium"
								>
									Email:
								</label>
								<span
									className={`text-sm ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									{email || '—'}
								</span>
							</div>
						</div>

						<hr className={`mb-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`} />

						<div className="flex items-center gap-3">
							<p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
								Want to delete your account?
							</p>
							<a
								href={`mailto:info@loadfetcher.com?subject=Delete my account&body=Please delete my account associated with email: ${encodeURIComponent(email)}`}
								target="_blank"
								rel="noopener noreferrer"
								className={`ml-4 whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded border ${
									isDarkMode
										? 'text-red-400 border-red-400/30 hover:bg-red-400/10'
										: 'text-red-500 border-red-300 hover:bg-red-50'
								}`}
							>
								Request Deletion
							</a>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default GeneralSettings
